import { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
// components
import ClassGridList from 'components/organisms/main/ClassGridList';
import PlanList from 'components/organisms/event/PlanList';
import ClassFilter from 'components/molecules/common/ClassFilter';
import Icon from 'cds/icons';
import IconButton from 'cds/buttons/Icon';
import Layout from 'components/layout';
import MainLayout from 'cds/layout/Main';
import VerticalScrollLayout from 'cds/layout/VerticalScroll';
import BothHeader from 'components/layout/headers/Both';
// slices
import { getEvent } from 'slices/event.thunk';
import { initPlan } from 'slices/event';
// hook
import { useSelector, useDispatch } from 'hooks/common/useStore';
import useCommonBridge from 'hooks/bridge/useCommonBridge';
import useClassFilter from 'hooks/domain/class/useFilter';
import useSearchModal from 'hooks/common/useSearchModal';
import useMetaData from 'hooks/common/useMetaData';
//utils
import { getShuffledArray } from 'lib/utils/array';
import { getKoreaDate } from 'lib/utils/format/time';
// styles
import { mediaQuery, mediaValue, palette } from 'cds/styles';
import * as typography from 'cds/typography';
import { setClickableInformation } from 'slices/dataMining';

/**
 * 월별 초저가 이벤트 페이지
 */
const MonthEventPage = () => {
  const router = useRouter();

  const dispatch = useDispatch();
  const [eventClassList] = useSelector(state => [state.event.plan.list]);
  const randomListAtFirstRender = useRef(true);

  const { MetaTitle } = useMetaData();
  const { currentFilter, FilterDropdown, FilterSheet, onFilterShow } = useClassFilter([
    0, 1, 2, 3, 4,
  ]);

  const [classList, setClassList] = useState([]);

  useEffect(() => {
    const title = document.head.querySelector('title').textContent;
    dispatch(
      setClickableInformation({
        [title]: classList,
      }),
    );
  }, [classList, dispatch]);

  const thisMonth = getKoreaDate().getMonth() + 1;

  const priceDecoder = val => {
    let price = val.replace(/,/g, '');
    return parseInt(price, 10);
  };

  const onSort = useCallback(
    sortId => {
      document.scrollingElement.scrollTop = 0;

      const arr = [...eventClassList];
      if (sortId === 0) {
        return arr.sort((a, b) => (a.signup_cnt > b.signup_cnt ? -1 : 1));
      }
      if (sortId === 1) {
        return arr.sort((a, b) => (a.star_avg > b.star_avg ? -1 : 1));
      }
      if (sortId === 2) {
        return arr.sort((a, b) => (a.star_count > b.star_count ? -1 : 1));
      }
      if (sortId === 3) {
        return arr.sort((a, b) =>
          priceDecoder(a.payment_price) < priceDecoder(b.payment_price) ? -1 : 1,
        );
      }
      if (sortId === 4) {
        return arr.sort((a, b) =>
          priceDecoder(a.payment_price) > priceDecoder(b.payment_price) ? -1 : 1,
        );
      }
    },
    [eventClassList],
  );

  useEffect(() => dispatch(getEvent({ dealId: 0 })), [dispatch]);

  useEffect(() => setClassList(getShuffledArray(eventClassList)), [eventClassList]);

  useEffect(() => {
    if (!classList.length) {
      return;
    }

    if (randomListAtFirstRender.current && currentFilter.name === '참여인기순') {
      return;
    }

    randomListAtFirstRender.current = false;
  }, [classList, currentFilter]);

  useEffect(() => {
    if (randomListAtFirstRender.current) {
      return;
    }

    const sorted = onSort(currentFilter.id);
    setClassList(sorted);
  }, [onSort, currentFilter]);

  useEffect(() => {
    const routerPath = router.asPath;

    const handleStart = url => {
      if (routerPath === url.replace(/^(\/new)/gi, '')) {
        return;
      }

      dispatch(initPlan());
    };

    router.events.on('routeChangeStart', handleStart);

    return () => {
      router.events.off('routeChangeStart', handleStart);
    };
  }, [dispatch, router.asPath, router.events]);

  return (
    <>
      <MetaTitle content={`${thisMonth}월의 초저가 기획전`} />

      <Wrapper>
        <TitleSection>{`${thisMonth}월의 초저가 기획전`}</TitleSection>

        <Section>
          <PlanList />
        </Section>

        <ClassFilter
          classTotalCount={eventClassList.length}
          sortName={currentFilter.name}
          onClickSort={onFilterShow}
        >
          <FilterDropdown />
        </ClassFilter>

        <ClassListLayout>
          <ClassGridList
            type="EVENT"
            list={classList}
            showSalePer
            showPrice
            slidePerView={[
              {
                break: 'default',
                view: 1,
              },
              {
                break: mediaValue.medium,
                view: 2,
              },
              {
                break: 1080,
                view: 3,
              },
              {
                break: 1480,
                view: 4,
              },
              {
                break: 1900,
                view: 5,
              },
            ]}
          />
        </ClassListLayout>
      </Wrapper>

      <FilterSheet />
    </>
  );
};

/**
 * 월별 초저가 이벤트 페이지 헤더
 */
const MonthEventHeader = () => {
  const router = useRouter();

  const { onCloseWebView, goToSearch } = useCommonBridge();
  const { onOpenSearch } = useSearchModal();

  const thisMonth = getKoreaDate().getMonth() + 1;

  /** 이전 단계 페이지 이동 함수 */
  const onBack = () => {
    if (!onCloseWebView()) {
      router.back();
    }
  };

  const onSearch = () => {
    if (!goToSearch()) {
      onOpenSearch();
    }
  };

  return (
    <BothHeader
      message={`${thisMonth}월의 초저가`}
      leftSide={
        <IconButton width={24} height={54} onClick={onBack}>
          <Icon name="ic_back_m" width={24} height={24} />
        </IconButton>
      }
      rightSide={
        <SearchButton onClick={onSearch}>
          <Icon name="ic_search" width={24} height={24} />
        </SearchButton>
      }
    />
  );
};

MonthEventPage.getLayout = function getLayout(page) {
  return <Layout mobileHeader={<MonthEventHeader />}>{page}</Layout>;
};

const Wrapper = styled(MainLayout)`
  width: 100%;

  ${mediaQuery.large} {
    width: 1800px;
    padding: 0 72px;
  }
`;

const ClassListLayout = styled(VerticalScrollLayout)`
  margin-top: 20px;

  & li {
    margin-top: 0;
  }

  ${mediaQuery.medium} {
    & li {
      margin-top: auto;
    }
  }

  ${mediaQuery.large} {
    padding-bottom: 128px;
  }
`;

const TitleSection = styled.div`
  display: none;
  align-items: center;
  justify-content: space-between;
  margin: 32px 0 8px 0;
  ${typography.subtitle3_medium};
  color: ${palette.grey75};

  ${mediaQuery.large} {
    display: flex;
  }
`;

const Section = styled.section`
  padding: 0 16px;

  ${mediaQuery.large} {
    padding: 0;
  }
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 auto;
  width: 24px;
  height: 54px;
`;

export default MonthEventPage;
